import api, { ApiResponse } from './index'
import {
    Notification,
    NotificationPreferences,
    NotificationAnalytics,
    DocumentVerificationNotification
} from '@/types/notification'

export const notificationApi = {
    // Get all notifications for current user
    getNotifications: (params: {
        page?: number
        limit?: number
        unreadOnly?: boolean
        type?: string
    } = {}): Promise<ApiResponse<{
        notifications: Notification[]
        unreadCount: number
        currentPage: number
        totalPages: number
        hasMore: boolean
    }>> =>
        api.get('/notifications', { params }),

    // Get single notification by ID
    getNotificationById: (id: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.get(`/notifications/${id}`),

    // Mark notification as read
    markAsRead: (id: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.put(`/notifications/${id}/read`),

    // Mark all notifications as read
    markAllAsRead: (): Promise<ApiResponse<{ count: number }>> =>
        api.put('/notifications/read-all'),

    // Delete notification
    deleteNotification: (id: string): Promise<ApiResponse> =>
        api.delete(`/notifications/${id}`),

    // Delete all notifications
    deleteAllNotifications: (): Promise<ApiResponse<{ count: number }>> =>
        api.delete('/notifications/all'),

    // Get unread notifications count
    getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
        api.get('/notifications/unread-count'),

    // Get notification preferences
    getPreferences: (): Promise<ApiResponse<{ data: { preferences: NotificationPreferences } }>> =>
        api.get('/notifications/preferences'),

    // Update notification preferences
    updatePreferences: (preferences: Partial<NotificationPreferences>): Promise<ApiResponse<{ data: { preferences: NotificationPreferences } }>> =>
        api.put('/notifications/preferences', preferences),

    // Subscribe to push notifications
    subscribeToPush: (subscription: PushSubscription): Promise<ApiResponse> =>
        api.post('/notifications/push/subscribe', { subscription }),

    // Unsubscribe from push notifications
    unsubscribeFromPush: (): Promise<ApiResponse> =>
        api.post('/notifications/push/unsubscribe'),

    // Test notification (for development)
    testNotification: (type: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.post('/notifications/test', { type }),

    // Get notification types and their descriptions
    getNotificationTypes: (): Promise<ApiResponse<{
        types: Array<{
            type: string
            name: string
            description: string
            category: string
        }>
    }>> =>
        api.get('/notifications/types'),

    // Snooze notification
    snoozeNotification: (
        id: string,
        snoozeUntil: string
    ): Promise<ApiResponse<{ notification: Notification }>> =>
        api.put(`/notifications/${id}/snooze`, { snoozeUntil }),

    // Unsnooze notification
    unsnoozeNotification: (id: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.put(`/notifications/${id}/unsnooze`),

    // Get notification statistics
    getNotificationStats: (dateRange?: {
        start: string
        end: string
    }): Promise<ApiResponse<{
        totalNotifications: number
        unreadNotifications: number
        readRate: number
        notificationsByType: Record<string, number>
        notificationsOverTime: Array<{ date: string; count: number }>
    }>> =>
        api.get('/notifications/stats', { params: dateRange }),

    // Bulk operations
    bulkMarkAsRead: (notificationIds: string[]): Promise<ApiResponse<{ count: number }>> =>
        api.put('/notifications/bulk-read', { notificationIds }),

    bulkDelete: (notificationIds: string[]): Promise<ApiResponse<{ count: number }>> =>
        api.delete('/notifications/bulk-delete', { data: { notificationIds } }),

    // Archive notification
    archiveNotification: (id: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.put(`/notifications/${id}/archive`),

    // Get archived notifications
    getArchivedNotifications: (params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        notifications: Notification[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/notifications/archived', { params }),

    // Restore archived notification
    restoreNotification: (id: string): Promise<ApiResponse<{ notification: Notification }>> =>
        api.put(`/notifications/${id}/restore`),

    // Get notification templates (for admin)
    getNotificationTemplates: (): Promise<ApiResponse<{
        templates: Array<{
            type: string
            subject: string
            body: string
            variables: string[]
        }>
    }>> =>
        api.get('/notifications/templates'),

    // Update notification template (for admin)
    updateNotificationTemplate: (
        type: string,
        template: {
            subject: string
            body: string
        }
    ): Promise<ApiResponse> =>
        api.put(`/notifications/templates/${type}`, template),

    // Document verification notification endpoints from Phase 2
    getVerificationNotifications: (params: {
        page?: number
        limit?: number
        documentType?: 'national_id' | 'passport' | 'drivers_license' | 'birth_certificate' | 'bank_statement' | 'utility_bill' | 'professional_certificate' | 'diploma'
        verificationStatus?: 'pending' | 'under_review' | 'verified' | 'rejected' | 'approved' | 'in_review'
        unreadOnly?: boolean
    } = {}): Promise<ApiResponse<{
        notifications: Notification[]
        pagination: {
            currentPage: number
            totalPages: number
            hasMore: boolean
        }
    }>> =>
        api.get('/notifications/verification', { params }),

    // Get notifications for specific document
    getDocumentNotifications: (documentId: string): Promise<ApiResponse<{
        notifications: Notification[]
        document: {
            _id: string
            documentType: string
            verificationStatus: string
            createdAt: string
        }
    }>> =>
        api.get(`/notifications/verification/${documentId}`),

    // Get verification notification statistics
    getVerificationStats: (params: {
        start?: string
        end?: string
    } = {}): Promise<ApiResponse<{
        totalVerificationNotifications: number
        unreadVerificationNotifications: number
        notificationsByStatus: Record<string, number>
        notificationsByDocumentType: Record<string, number>
        readRate: number
    }>> =>
        api.get('/notifications/verification/stats', { params }),

    // Phase 4: Notification Preferences API
    getNotificationPreferences: (): Promise<ApiResponse<{ data: { preferences: NotificationPreferences } }>> =>
        api.get('/notifications/preferences'),

    updateNotificationPreferences: (preferences: Partial<NotificationPreferences>): Promise<ApiResponse<{ data: { preferences: NotificationPreferences } }>> =>
        api.put('/notifications/preferences', preferences),

    // Phase 4: Advanced Analytics API
    getNotificationAnalytics: (params: {
        start?: string
        end?: string
        userId?: string
        type?: 'verification' | 'general'
    } = {}): Promise<ApiResponse<NotificationAnalytics>> =>
        api.get('/notifications/analytics', { params }),

    // Phase 4: Track notification engagement
    trackNotificationEngagement: (notificationId: string, action: 'opened' | 'clicked' | 'dismissed'): Promise<ApiResponse<void>> =>
        api.post(`/notifications/${notificationId}/track`, { action }),

    // Phase 4: Rich notification content
    getNotificationWithRichContent: (notificationId: string): Promise<ApiResponse<DocumentVerificationNotification>> =>
        api.get(`/notifications/${notificationId}/rich`),
}